import {
  clearUserToken,
  getAccessToken,
  getSubId,
} from '@devhacker/shared/features/auth/utils/token';
import { decodeJWT } from '@devhacker/shared/features/auth/utils/jwt';
import type { User } from '@devhacker/shared/features/user/types/user';
import PageScroll from '~/libs/PageScroll';

import { useReactionsStore } from './reactions';
import { useArticlesStore } from './articles';
import { useFoldersStore } from './folders';
import { useTalkerStore } from '@devhacker/shared/plugins/talker/store/talker';
import { defineStore } from 'pinia';
import { wrapPiniaStore } from '@devhacker/shared/utils/wrapPiniaStore';
import { isRequiresAuth } from '~/utils';
import {
  deleteAccount,
  fetchIdentityUser,
  logout,
  requestEmailChange as requestEmailChangeApi,
  resendEmailConfirmation as resendEmailConfirmationApi,
  unlinkSocialAccount,
} from '@devhacker/shared/features/auth/api';
import {
  deleteProfileLink as deleteProfileLinkApi,
  fetchPreferences as fetchPreferencesApi,
  fetchProfileUser,
  patchPreferences,
  patchProfileUser,
  storeProfileLink,
  updateProfileLink as updateProfileLinkApi,
} from '@devhacker/shared/features/profile/api';
import { withProfileRetry } from '@devhacker/shared/features/profile/useProfileFallback';
import { mergeIdentityAndProfile } from '@devhacker/shared/features/user/mergeIdentityAndProfile';
import type { ProfileLinkType } from '@devhacker/shared/features/profile/schema';

// TODO: split into multiple stores, user|ui|etc.
export const useIndexStore = wrapPiniaStore(
  defineStore('index', {
    state: (): IRootState => ({
      visibleModalWindows: [],
      user: null,
      isUserLoading: false,
      userIntegerId: null,
      isAuthenticatedInWordpress: false,
      isSubscribedToWebPushNotifications: false,
      isHeadScriptsLoaded: false,
      isSendRefreshToken: false,
      afterLoginAction: undefined,
    }),

    actions: {
      /** Проверка cookie редактора WordPress через GET /api/logged; гейт AdminControlsPanel */
      async fetchIsWpLoggedIn() {
        const { $wordpressApi } = useNuxtApp();
        try {
          const { data } = await $wordpressApi.fetchIsLoggedInWordpress();
          this.updateWordpressAuthentication(data.logged ?? false);
        } catch (err) {
          console.error('fetchIsLoggedInWordpress', err);
        }
      },

      updateWordpressAuthentication(isLoggedIn: boolean) {
        this.isAuthenticatedInWordpress = isLoggedIn;
      },

      async fetchUser(subId: string) {
        const accessToken = getAccessToken();
        this.isUserLoading = true;

        try {
          const [identityResp, profileResp] = await Promise.allSettled([
            fetchIdentityUser(subId),
            withProfileRetry(() => fetchProfileUser(subId)),
          ]);

          const mergedUser = mergeIdentityAndProfile({
            integerId: subId,
            identity: identityResp.status === 'fulfilled' ? identityResp.value : null,
            profile: profileResp.status === 'fulfilled' ? profileResp.value : null,
            bannedAt: accessToken ? (decodeJWT(accessToken)?.banned_at ?? null) : null,
          });

          this.updateUser(mergedUser);
          return mergedUser;
        } finally {
          this.isUserLoading = false;
        }
      },

      showNav() {
        this.visibleModalWindows = this.visibleModalWindows.filter(
          (visible) => visible === 'search',
        );
        this.visibleModalWindows.push('nav');
        PageScroll.disable();
      },

      hideNav() {
        this.visibleModalWindows = this.visibleModalWindows.filter((visible) => visible !== 'nav');
        PageScroll.enable();
      },

      updateUser(user: User) {
        this.user = Object.seal(user);
      },

      patchUser(userData: Partial<User>) {
        if (!this.user) return;
        this.user = { ...this.user, ...userData };
      },

      updateUserProperty({ property, value }: { property: keyof User; value: unknown }) {
        if (this.user) {
          // TODO: check weird typescript without typecast of property
          (this.user[property] as typeof value) = value;
        }
      },

      clearUser() {
        this.user = null;
        this.userIntegerId = null;
      },

      clearUserAndRelatedData() {
        this.clearUser();
        useArticlesStore().clearUserRelatedData();
        useFoldersStore().clearFolders();
      },

      subscribeToWebPushNotifications() {
        this.isSubscribedToWebPushNotifications = true;
      },

      unsubscribeFromWebPushNotifications() {
        this.isSubscribedToWebPushNotifications = false;
      },

      updateIsHeadScriptsLoaded(isHeadScriptsLoaded: boolean) {
        this.isHeadScriptsLoaded = isHeadScriptsLoaded;
      },

      addIntegerIdUser(subId: string) {
        this.userIntegerId = subId;
      },

      setUserRTAction(isSend: boolean) {
        this.isSendRefreshToken = isSend;
      },

      clearSessionLocally() {
        clearUserToken();
        this.clearUserAndRelatedData();
        useTalkerStore().clearUserData();
      },

      async logout() {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        try {
          await logout();
        } catch (error) {
          console.error('logout failed', error);
        }

        this.clearSessionLocally();

        if (isSafari) {
          window.location.reload();
        } else if (isRequiresAuth(useRoute())) {
          location.href = '/';
        }
      },

      async updateUserWithReactions(payload: { user: User }) {
        this.updateUser(payload.user);
        this.addIntegerIdUser(payload.user.integer_id);
        await useReactionsStore().refetchReactions();
      },

      async clearUserAndReactions() {
        this.clearUser();
        useReactionsStore().refetchReactions();
      },

      /**
       * Смена email через double opt-in (auth-sso): новый адрес паркуется в email_temp
       * до подтверждения по ссылке из письма, поле email остаётся прежним.
       */
      requestEmailChange(email: string) {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return requestEmailChangeApi(subId, { email, redirect_url: location.href }).then(
          (identityUser) => {
            this.patchUser({ email_temp: email });
            return identityUser;
          },
        );
      },

      /** Повторная отправка письма-подтверждения для адреса из email_temp (auth-sso) */
      resendEmailConfirmation() {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return resendEmailConfirmationApi(subId, { redirect_url: location.href });
      },

      // Обновление профильных данных (display_name, about, social links) через profile API
      patchProfileProperty(payload: { property: string; value: unknown }) {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return patchProfileUser(subId, { [payload.property]: payload.value }).then(
          (profileUser) => {
            this.updateUserProperty(payload as { property: keyof User; value: unknown });
            return profileUser;
          },
        );
      },

      createProfileLink(payload: { type: ProfileLinkType; url: string }) {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return storeProfileLink(subId, payload).then((link) => {
          if (link && this.user) {
            const current = this.user.links || [];
            this.patchUser({ links: [...current, link] });
          }
          return link;
        });
      },

      updateProfileLink(linkId: number, payload: { url?: string; type?: ProfileLinkType }) {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return updateProfileLinkApi(subId, linkId, payload).then((link) => {
          if (link && this.user) {
            const current = this.user.links || [];
            this.patchUser({
              links: current.map((item) => (item.id === linkId ? link : item)),
            });
          }
          return link;
        });
      },

      deleteProfileLink(linkId: number) {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return deleteProfileLinkApi(subId, linkId).then(() => {
          if (this.user) {
            const current = this.user.links || [];
            this.patchUser({ links: current.filter((item) => item.id !== linkId) });
          }
        });
      },

      fetchPreferences(): Promise<Record<string, boolean>> | undefined {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return fetchPreferencesApi<{ data?: Record<string, boolean> }>(subId).then(
          (resp) => resp?.data ?? {},
        );
      },

      patchPreference(payload: { key: string; value: boolean }) {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return patchPreferences(subId, { [payload.key]: payload.value });
      },

      disableSocialAccount(payload: string) {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return unlinkSocialAccount(subId, payload);
      },

      deleteUserAccount(reason: string | null) {
        const token = getAccessToken();
        if (!token) return;

        const subId = getSubId(token);
        if (!subId) return;

        return deleteAccount(subId, reason).then(() => true);
      },
    },
  }),
);
